import LegalShell from "./LegalShell";

/**
 * Google API Services User Data Policy — Limited Use disclosure.
 * Required by Google as a public page before their OAuth Trust & Safety team
 * will approve access to sensitive scopes (calendar.events, in our case).
 *
 * The section titled “Limited Use” below MUST contain, verbatim, the
 * affirmative statement Google's verification form asks us to publish.
 * Do not paraphrase — verification bots literally string-match this.
 */
export default function LimitedUse() {
  return (
    <LegalShell
      title="Google API Services — Limited Use"
      updated="10 February 2026"
      path="/legal/limited-use"
      description="Viyora Tech — Google API Services User Data Policy Limited Use disclosure covering Google Calendar integration scopes."
    >
      <p>
        This page describes how Viyora (operated by <strong>Viyora Tech</strong>) uses
        data received from Google APIs when a clinic connects their Google
        Calendar to Viyora. It is published in compliance with Google&rsquo;s{" "}
        <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noreferrer">
          Google API Services User Data Policy
        </a>
        , including the Limited Use requirements for Workspace and consumer
        Google accounts.
      </p>

      <h2>1. Which Google scopes we request</h2>
      <p>
        When a doctor or clinic administrator clicks &ldquo;Connect Google
        Calendar&rdquo; inside Viyora, we request only the narrowest scopes
        needed for two-way appointment synchronisation:
      </p>
      <ul>
        <li>
          <code>https://www.googleapis.com/auth/calendar.events</code>{" "}
          &mdash; view, create, edit and delete events on the calendars the
          connecting user chooses. This is a <em>sensitive</em> scope. We do
          not receive or store any events from calendars the user does not
          explicitly select in Settings &rarr; Google Calendar.
        </li>
        <li>
          <code>https://www.googleapis.com/auth/calendar.calendarlist.readonly</code>{" "}
          &mdash; read the list of calendars the user is subscribed to, purely
          so the &ldquo;Which calendar should Viyora write to?&rdquo; picker
          can populate. This is a non-sensitive scope.
        </li>
        <li>
          <code>openid</code>, <code>email</code>, <code>profile</code>{" "}
          &mdash; to display the connected Google account&rsquo;s email in
          Viyora Settings so doctors can verify they linked the right account.
        </li>
      </ul>
      <p>
        We <strong>do not</strong> request full <code>auth/calendar</code>{" "}
        access, <code>gmail.*</code> scopes, <code>drive</code> scopes,{" "}
        <code>contacts</code> scopes, or any other Google user data scope.
      </p>

      <h2>2. How we use Google Calendar data</h2>
      <ul>
        <li>
          <strong>Push (Viyora &rarr; Google):</strong> When a receptionist
          books, reschedules, or cancels a chairside appointment in Viyora,
          we mirror that change to the doctor&rsquo;s selected Google Calendar
          so their personal calendar stays in sync with the clinic.
        </li>
        <li>
          <strong>Pull (Google &rarr; Viyora):</strong> Every five minutes we
          import events the doctor added directly to Google (e.g. holidays,
          conference days, personal appointments) into a private{" "}
          <code>google_busy_events</code> table. Viyora uses those events{" "}
          <em>only</em> to strip conflicting slots from online-booking
          availability &mdash; so patients never book a slot when the doctor
          is unavailable.
        </li>
        <li>
          <strong>Event titles &amp; descriptions</strong> pulled from Google
          are shown to the doctor and receptionists inside their own clinic
          only. They are never shown to patients, never rendered publicly,
          and never used to train models.
        </li>
      </ul>

      <h2>3. Limited Use</h2>
      <p>
        <strong>
          Viyora's use and transfer of information received from Google
          APIs to any other app will adhere to the{" "}
          <a
            href="https://developers.google.com/terms/api-services-user-data-policy"
            target="_blank"
            rel="noreferrer"
          >
            Google API Services User Data Policy
          </a>
          , including the Limited Use requirements.
        </strong>
      </p>
      <p>Specifically:</p>
      <ul>
        <li>
          We use Google user data <strong>only</strong> to provide and
          improve the calendar synchronisation feature described above.
        </li>
        <li>
          We <strong>do not</strong> transfer Google user data to third
          parties except (a) as necessary to provide the feature (e.g. our
          own Kubernetes-hosted MongoDB, which is subject to the same
          Limited Use terms), (b) when required by applicable law, or (c) as
          part of a merger or acquisition where the acquirer commits to the
          same Limited Use terms.
        </li>
        <li>
          We <strong>do not</strong> use Google user data for serving
          advertisements, retargeting, or building any advertising profile.
        </li>
        <li>
          We <strong>do not</strong> allow humans to read Google user data
          unless (a) we have the affected user&rsquo;s explicit written
          consent for a specific message, (b) we require access for security
          purposes such as investigating abuse, (c) we require access to
          comply with applicable law, or (d) the data (including derived
          data) has been aggregated and de-identified.
        </li>
        <li>
          We <strong>do not</strong> use Google user data to train, fine-tune
          or evaluate generalised or third-party AI/ML models. Claude
          Sonnet 4.5 (via the Emergent LLM proxy) is used for clinical
          reasoning on Viyora-native data (patient notes, treatment plans)
          only &mdash; never on data received from Google APIs.
        </li>
      </ul>

      <h2>4. Storage &amp; encryption</h2>
      <ul>
        <li>
          Google OAuth refresh tokens are encrypted at rest using Fernet
          (AES-128-CBC + HMAC-SHA256) with a per-deployment secret derived
          from our JWT signing key. A MongoDB dump alone is not sufficient
          to replay a token.
        </li>
        <li>
          Imported busy events are scoped by both{" "}
          <code>clinic_id</code> and <code>doctor_id</code>, and are only
          served to authenticated members of that clinic.
        </li>
        <li>
          Transport is TLS 1.2+ end-to-end between Viyora, Google APIs, and
          the connecting user&rsquo;s browser.
        </li>
      </ul>

      <h2>5. Revocation &amp; deletion</h2>
      <p>
        A doctor can disconnect Google Calendar at any time from{" "}
        <em>Settings &rarr; Integrations &rarr; Google Calendar &rarr;
        Disconnect</em>. On disconnect we immediately:
      </p>
      <ul>
        <li>Delete the encrypted refresh token from our database.</li>
        <li>
          Delete all imported <code>google_busy_events</code> rows for that
          doctor.
        </li>
        <li>
          Stop all scheduled sync jobs for that doctor within five minutes.
        </li>
      </ul>
      <p>
        You can additionally revoke Viyora&rsquo;s access from your Google
        account at{" "}
        <a
          href="https://myaccount.google.com/permissions"
          target="_blank"
          rel="noreferrer"
        >
          myaccount.google.com/permissions
        </a>
        .
      </p>

      <h2>6. Contact</h2>
      <p>
        For any questions about how Viyora handles data received from Google
        APIs, or to file a Limited Use compliance concern, please contact:
      </p>
      <p>
        Viyora Tech
        <br />
        Email: <a href="mailto:admin@viyora.co.in">admin@viyora.co.in</a>
        <br />
        Website: <a href="https://viyora.co.in">https://viyora.co.in</a>
      </p>
    </LegalShell>
  );
}
